// components/Benchmark/ProductIntelligence.jsx
// Product-level price intelligence — how the price of each individual
// product or service has shifted, tender by tender.
import React, { useMemo, useState } from "react";
import { Button, Field, Select, Text, Tooltip } from "@fluentui/react-components";
import {
  Dismiss24Regular, ArrowTrendingLines24Regular, Box24Regular,
  People24Regular, Search24Regular,
  ArrowSortDown24Regular, Eye24Regular, Comment24Regular,
} from "@fluentui/react-icons";
import {
  buildProductIntelligence, productPortfolioStats, topMovers,
  generateProductInsights, compareProducts, shouldCost,
  fmtPct, fmtSignedPct, fmtMoney, fmtCompact,
} from "../../services/priceIntelligenceService";
import {
  GOLD, GOLD_LT, NAVY, GOOD, WARN, BAD, MUTED, SERIES_COLORS,
  Panel, KPI, Empty, ConfidenceChip, InsightList, LineChart, ChangeBadge,
  th, td,
} from "./intelUI";
import { usePagination, Pagination, useDebounced } from "../common/Pagination";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

function catColor(cat, cats) {
  const i = cats.indexOf(cat);
  return SERIES_COLORS[(i < 0 ? 0 : i) % SERIES_COLORS.length];
}

// ── Mover card ────────────────────────────────────────────────────────────────

function MoverRow({ p, onOpen, tone }) {
  const color = tone === "up" ? BAD : tone === "down" ? GOOD : WARN;
  const metric = tone === "vol" ? p.volatility : p.moveRate;
  return (
    <button
      onClick={() => onOpen(p.key)}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%",
        padding: "10px 12px", borderRadius: 9, border: "1px solid #EEF0F4",
        background: "#fff", cursor: "pointer", textAlign: "left", fontFamily: "inherit",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "#FFFBF2"; e.currentTarget.style.borderColor = "#FFE8B8"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#EEF0F4"; }}
    >
      <div style={{ width: 3, alignSelf: "stretch", borderRadius: 2, background: color, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p.name}
        </div>
        <div style={{ fontSize: 10.5, color: MUTED, marginTop: 1 }}>
          {p.category}{p.unitOfMeasure ? ` · ${p.unitOfMeasure}` : ""} · {p.n} awards
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>
          {tone === "vol" ? `±${fmtPct(metric)}` : fmtSignedPct(metric)}
        </div>
        <div style={{ fontSize: 10, color: MUTED }}>
          {tone === "vol" ? "spread" : p.moveBasis}
        </div>
      </div>
    </button>
  );
}

// ── Supplier bar ──────────────────────────────────────────────────────────────

function SupplierBars({ suppliers, currency, median }) {
  const maxAwards = Math.max(...suppliers.map((s) => s.awards), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {suppliers.map((s) => {
        const rel = s.relIndex;
        const relColor = rel === null ? MUTED : rel < 97 ? GOOD : rel > 103 ? BAD : "#666";
        return (
          <div key={s.name}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.name}
              </span>
              <span style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap", flexShrink: 0 }}>
                {s.awards} award{s.awards === 1 ? "" : "s"} · avg {fmtMoney(s.avgRate, currency, 0)}
                {rel !== null && (
                  <span style={{ marginLeft: 8, color: relColor, fontWeight: 700 }}>
                    index {rel.toFixed(0)}
                  </span>
                )}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, height: 10, background: "#F3F4F6", borderRadius: 5, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${(s.awards / maxAwards) * 100}%`, background: GOLD_LT, borderRadius: 5 }} />
              </div>
              <span style={{ fontSize: 10.5, color: MUTED, minWidth: 38, textAlign: "right" }}>
                {(s.share * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        );
      })}
      {median > 0 && (
        <Text style={{ fontSize: 11, color: MUTED, lineHeight: 1.6, marginTop: 2 }}>
          Index compares each supplier's average rate against the product median of {fmtMoney(median, currency, 0)}.
          Below 100 means they price cheaper than the line as a whole.
        </Text>
      )}
    </div>
  );
}

// ── Product deep dive ─────────────────────────────────────────────────────────

function ProductDetail({ product: p, records, onClose }) {
  const sc = useMemo(() => shouldCost(records, {
    category: p.category,
    commodityGroup: p.name,
    unitOfMeasure: p.unitOfMeasure,
    currency: p.currency,
    targetDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().slice(0, 10),
  }), [records, p]);

  const years = Object.keys(p.byYear).sort();

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.22)", zIndex: 290, backdropFilter: "blur(2px)" }} />
      <div style={{
        position: "fixed", top: 0, right: 0, width: 660, maxWidth: "100vw", height: "100vh",
        background: "#fff", zIndex: 300, boxShadow: "-4px 0 32px rgba(0,0,0,0.14)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 24px 15px", borderBottom: "1px solid #EDEBE9", background: "#FFFBF2", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 7, flexWrap: "wrap" }}>
                <span style={{ background: `${GOLD}18`, color: GOLD, padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{p.category}</span>
                {p.unitOfMeasure && <span style={{ background: "#EEF2F7", color: "#556", padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{p.unitOfMeasure}</span>}
                <span style={{ background: "#EEF2F7", color: "#556", padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{p.currency}</span>
                <ConfidenceChip confidence={p.confidence} />
              </div>
              <Text size={500} weight="bold" style={{ color: NAVY, lineHeight: 1.3, display: "block" }}>{p.name}</Text>
              <Text style={{ fontSize: 11.5, color: MUTED, display: "block", marginTop: 3 }}>
                {p.n} award{p.n === 1 ? "" : "s"} · {fmtDate(p.firstDate)} to {fmtDate(p.lastDate)}
                {p.locations.length > 0 && ` · ${p.locations.slice(0, 3).join(", ")}`}
              </Text>
            </div>
            <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} style={{ flexShrink: 0 }} />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          {/* Headline numbers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 18 }}>
            {[
              { label: "LATEST RATE", value: fmtMoney(p.latestRate, p.currency, 0), sub: fmtDate(p.lastDate), color: GOLD },
              {
                label: "LAST MOVE",
                value: p.latestChange === null ? "—" : fmtSignedPct(p.latestChange),
                sub: p.previousRate ? `from ${fmtMoney(p.previousRate, p.currency, 0)}` : "first award",
                color: p.latestChange > 0 ? BAD : p.latestChange < 0 ? GOOD : "#666",
              },
              {
                label: "ANNUALISED",
                value: p.cagr === null ? "—" : fmtSignedPct(p.cagr),
                sub: p.spanYears >= 0.5 ? `over ${p.spanYears.toFixed(1)} yrs` : "history too short",
                color: p.cagr > 0 ? BAD : p.cagr < 0 ? GOOD : "#666",
              },
              {
                label: "SINCE FIRST AWARD",
                value: p.totalChange === null ? "—" : fmtSignedPct(p.totalChange),
                sub: `from ${fmtMoney(p.firstRate, p.currency, 0)}`,
                color: p.totalChange > 0 ? BAD : p.totalChange < 0 ? GOOD : "#666",
              },
            ].map((b) => (
              <div key={b.label} style={{ background: "#F8F9FC", borderRadius: 9, padding: "11px 13px" }}>
                <Text style={{ fontSize: 9.5, fontWeight: 700, color: MUTED, letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>{b.label}</Text>
                <Text style={{ fontSize: 15, fontWeight: 800, color: b.color, display: "block", fontVariantNumeric: "tabular-nums" }}>{b.value}</Text>
                <Text style={{ fontSize: 10, color: MUTED, display: "block", marginTop: 2 }}>{b.sub}</Text>
              </div>
            ))}
          </div>

          {/* Price history */}
          {p.n > 1 && (
            <>
              <SectionLabel>Price history</SectionLabel>
              <LineChart
                height={230}
                yFormat={(v) => fmtCompact(v)}
                series={[{
                  label: p.name,
                  color: GOLD_LT,
                  xLabel: (x) => new Date(x).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
                  points: p.awards.map((a) => ({
                    x: a.t, y: a.rate,
                    tip: `${a.tenderRef}\n${fmtDate(a.date)}\n${fmtMoney(a.rate, p.currency)}\n${a.supplier}${a.changeFromPrev !== null ? `\n${fmtSignedPct(a.changeFromPrev)} on previous` : ""}`,
                  })),
                }]}
              />
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8, marginBottom: 18 }}>
                {[
                  ["Range", `${fmtMoney(p.min, p.currency, 0)} – ${fmtMoney(p.max, p.currency, 0)}`],
                  ["Median", fmtMoney(p.median, p.currency, 0)],
                  ["Volatility", p.volatility === null ? "—" : `±${fmtPct(p.volatility)}`],
                  ["Trend fit", p.trend ? `r² ${p.trend.r2.toFixed(2)}` : "—"],
                ].map(([k, v]) => (
                  <span key={k} style={{ fontSize: 11.5, color: MUTED }}>
                    {k}: <strong style={{ color: NAVY }}>{v}</strong>
                  </span>
                ))}
              </div>
            </>
          )}

          {/* Award-by-award */}
          <SectionLabel>Every award, and what moved</SectionLabel>
          <div style={{ overflowX: "auto", marginBottom: 18, border: "1px solid #EEF0F4", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: NAVY }}>
                  <th style={th("left")}>DATE</th>
                  <th style={th("left")}>TENDER</th>
                  <th style={th("left")}>SUPPLIER</th>
                  <th style={th("right")}>RATE</th>
                  <th style={th("center")}>MOVE</th>
                  <th style={th("center")}>VS BENCHMARK</th>
                </tr>
              </thead>
              <tbody>
                {[...p.awards].reverse().map((a, i) => (
                  <tr key={a.tenderRef + i} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 ? "#FAFBFC" : "#fff" }}>
                    <td style={td("left", { whiteSpace: "nowrap" })}>{fmtDate(a.date)}</td>
                    <td style={td("left", { fontWeight: 600, color: NAVY })}>{a.tenderRef}</td>
                    <td style={td("left")}>{a.supplier}</td>
                    <td style={td("right", { fontWeight: 700, color: GOLD, whiteSpace: "nowrap" })}>{fmtMoney(a.rate, p.currency, 0)}</td>
                    <td style={td("center")}><ChangeBadge value={a.changeFromPrev} /></td>
                    <td style={td("center")}>
                      {a.vsBenchmark === null
                        ? <span style={{ color: "#CCC" }}>—</span>
                        : <ChangeBadge value={a.vsBenchmark} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* By year */}
          {years.length > 1 && (
            <>
              <SectionLabel>Year on year</SectionLabel>
              <div style={{ overflowX: "auto", marginBottom: 18, border: "1px solid #EEF0F4", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: NAVY }}>
                      <th style={th("left")}>YEAR</th>
                      <th style={th("right")}>AWARDS</th>
                      <th style={th("right")}>MEDIAN RATE</th>
                      <th style={th("right")}>RANGE</th>
                      <th style={th("center")}>SHIFT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {years.map((y, i) => {
                      const c = p.byYear[y];
                      return (
                        <tr key={y} style={{ borderBottom: "1px solid #EEF0F4", background: i % 2 ? "#FAFBFC" : "#fff" }}>
                          <td style={td("left", { fontWeight: 700, color: NAVY })}>{y}</td>
                          <td style={td("right")}>{c.n}</td>
                          <td style={td("right", { fontWeight: 700, color: GOLD })}>{fmtMoney(c.median, p.currency, 0)}</td>
                          <td style={td("right", { fontSize: 11, color: MUTED, whiteSpace: "nowrap" })}>
                            {c.min === c.max ? "—" : `${fmtCompact(c.min)} – ${fmtCompact(c.max)}`}
                          </td>
                          <td style={td("center")}>{c.change === null ? <span style={{ color: "#CCC" }}>base</span> : <ChangeBadge value={c.change} />}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Suppliers */}
          <SectionLabel>
            Who supplies this
            <span style={{ marginLeft: 8, fontWeight: 400, color: MUTED, fontSize: 11 }}>
              {p.concentrationLabel.level} — {p.concentrationLabel.note}
            </span>
          </SectionLabel>
          <div style={{ marginBottom: 18 }}>
            <SupplierBars suppliers={p.suppliers} currency={p.currency} median={p.median} />
          </div>

          {/* Should-cost */}
          {sc.available && (
            <>
              <SectionLabel>What the next one should cost</SectionLabel>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, marginBottom: 8 }}>
                {[
                  { label: "STRETCH", v: sc.estimate.low, c: GOOD },
                  { label: "TARGET", v: sc.estimate.target, c: GOLD },
                  { label: "WALK-AWAY", v: sc.estimate.high, c: WARN },
                ].map((b) => (
                  <div key={b.label} style={{ background: `${b.c}0D`, border: `1.5px solid ${b.c}33`, borderRadius: 9, padding: "10px 12px" }}>
                    <Text style={{ fontSize: 9, fontWeight: 700, color: b.c, letterSpacing: "0.4px", display: "block" }}>{b.label}</Text>
                    <Text style={{ fontSize: 13.5, fontWeight: 800, color: NAVY, fontVariantNumeric: "tabular-nums" }}>{fmtMoney(b.v, p.currency, 0)}</Text>
                  </div>
                ))}
              </div>
              <Text style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.65, display: "block", marginBottom: 18 }}>
                Escalated {sc.escalation.yearsOut.toFixed(2)} years from the last award at {fmtSignedPct(sc.escalation.rate)} a year,
                the measured inflation rate for {p.category}.
              </Text>
            </>
          )}

          {/* Market observations */}
          {p.observations.length > 0 && (
            <>
              <SectionLabel>Market observations recorded against this product</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 12 }}>
                {p.observations.map((o, i) => (
                  <div key={i} style={{ background: "#F8F9FC", borderLeft: `3px solid ${GOLD_LT}`, borderRadius: "0 8px 8px 0", padding: "10px 14px" }}>
                    <div style={{ fontSize: 10.5, color: MUTED, marginBottom: 4 }}>
                      {fmtDate(o.date)} · {o.tenderRef} · {o.supplier}
                    </div>
                    <Text style={{ fontSize: 12.5, color: "#444", lineHeight: 1.65 }}>{o.text}</Text>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Descriptions seen */}
          {p.descriptions.length > 0 && (
            <>
              <SectionLabel>Described in tenders as</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {p.descriptions.map((d, i) => (
                  <Text key={i} style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>• {d}</Text>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10.5, fontWeight: 800, color: GOLD, letterSpacing: "0.6px",
      textTransform: "uppercase", marginBottom: 10, paddingBottom: 6,
      borderBottom: "1px solid #FFE8B8",
    }}>
      {children}
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

const SORTS = {
  awards:    { label: "Most awards",        fn: (a, b) => b.n - a.n },
  movement:  { label: "Biggest rise",       fn: (a, b) => (b.cagr ?? b.latestChange ?? -99) - (a.cagr ?? a.latestChange ?? -99) },
  falling:   { label: "Biggest fall",       fn: (a, b) => (a.cagr ?? a.latestChange ?? 99) - (b.cagr ?? b.latestChange ?? 99) },
  volatile:  { label: "Most volatile",      fn: (a, b) => (b.volatility ?? -1) - (a.volatility ?? -1) },
  value:     { label: "Highest rate",       fn: (a, b) => b.latestRate - a.latestRate },
  recent:    { label: "Most recent award",  fn: (a, b) => (b.lastDate || "").localeCompare(a.lastDate || "") },
  name:      { label: "Name A–Z",           fn: (a, b) => a.name.localeCompare(b.name) },
};

export default function ProductIntelligenceTab({ records }) {
  const products = useMemo(() => buildProductIntelligence(records), [records]);
  const stats = useMemo(() => productPortfolioStats(records), [records]);
  const movers = useMemo(() => topMovers(records, 5), [records]);
  const insights = useMemo(() => generateProductInsights(records), [records]);

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category))].sort(),
    [products],
  );

  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [onlyTracked, setOnlyTracked] = useState(false);
  const [sort, setSort] = useState("awards");
  const [openKey, setOpenKey] = useState(null);
  const [compareKeys, setCompareKeys] = useState([]);

  const debouncedSearch = useDebounced(search, 220);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return products
      .filter((p) => {
        if (filterCat !== "All" && p.category !== filterCat) return false;
        if (onlyTracked && p.n < 2) return false;
        if (!q) return true;
        return (
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.unitOfMeasure.toLowerCase().includes(q) ||
          p.suppliers.some((s) => s.name.toLowerCase().includes(q)) ||
          p.descriptions.some((d) => d.toLowerCase().includes(q))
        );
      })
      .sort(SORTS[sort].fn);
  }, [products, debouncedSearch, filterCat, onlyTracked, sort]);

  const pager = usePagination(filtered, {
    defaultPageSize: 50,
    resetKey: `${filterCat}|${onlyTracked}|${sort}|${debouncedSearch}`,
  });

  const openProduct = products.find((p) => p.key === openKey) || null;
  const comparison = useMemo(
    () => (compareKeys.length ? compareProducts(records, compareKeys) : []),
    [records, compareKeys],
  );

  function toggleCompare(key) {
    setCompareKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : prev.length >= 5 ? prev : [...prev, key],
    );
  }

  if (!products.length) {
    return <Panel><Empty>No products recorded yet in this period.</Empty></Panel>;
  }

  return (
    <>
      {/* KPIs */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <KPI label="Products tracked" value={stats.total}
          sub={`${stats.tracked} with repeat awards`} Icon={Box24Regular} />
        <KPI label="Avg annual price move" value={stats.avgAnnualMove === null ? "—" : fmtSignedPct(stats.avgAnnualMove)}
          sub="across products with history"
          color={stats.avgAnnualMove > 0 ? BAD : GOOD} Icon={ArrowTrendingLines24Regular} />
        <KPI label="Single-sourced" value={stats.singleSourced}
          sub="repeat products, one supplier"
          color={stats.singleSourced > 0 ? WARN : GOOD} Icon={People24Regular} />
        <KPI label="Competitively bid" value={stats.competitive}
          sub="three or more suppliers" color={GOOD} Icon={People24Regular} />
      </div>

      {/* Briefing */}
      <Panel title="Product briefing" sub="What the price history of individual products is saying right now.">
        <InsightList insights={insights} />
      </Panel>

      {/* Movers */}
      {(movers.risers.length > 0 || movers.fallers.length > 0 || movers.volatile.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16, marginBottom: 16 }}>
          {movers.risers.length > 0 && (
            <Panel title="Rising fastest" sub="Where price pressure is concentrated" pad="18px 20px">
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {movers.risers.map((p) => <MoverRow key={p.key} p={p} tone="up" onOpen={setOpenKey} />)}
              </div>
            </Panel>
          )}
          {movers.fallers.length > 0 && (
            <Panel title="Falling" sub="Buying opportunities — keep terms short" pad="18px 20px">
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {movers.fallers.map((p) => <MoverRow key={p.key} p={p} tone="down" onOpen={setOpenKey} />)}
              </div>
            </Panel>
          )}
          {movers.volatile.length > 0 && (
            <Panel title="Widest price spread" sub="Dispersion is negotiating room" pad="18px 20px">
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {movers.volatile.map((p) => <MoverRow key={p.key} p={p} tone="vol" onOpen={setOpenKey} />)}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* Comparison */}
      {comparison.length > 0 && (
        <Panel
          title={`Comparing ${comparison.length} product${comparison.length === 1 ? "" : "s"}`}
          sub="Each product is rebased to 100 at its own first award, so lines priced in different units and currencies can still be read against each other."
          right={<Button appearance="subtle" size="small" onClick={() => setCompareKeys([])} style={{ color: MUTED }}>Clear</Button>}
        >
          <LineChart
            height={280}
            baseline={100}
            yFormat={(v) => v.toFixed(0)}
            series={comparison.map((c, i) => ({
              label: `${c.name}${c.unitOfMeasure ? ` (${c.unitOfMeasure})` : ""}`,
              color: SERIES_COLORS[i % SERIES_COLORS.length],
              xLabel: (x) => new Date(x).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
              points: c.points,
            }))}
          />
        </Panel>
      )}

      {/* Catalogue */}
      <Panel
        title="Product catalogue"
        sub="Every distinct product line, with how its price has moved. Tick the compare box on any row to chart them together."
        right={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <Search24Regular style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#999", fontSize: 15 }} />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search product, supplier, description…"
                style={{ padding: "7px 10px 7px 32px", border: "1px solid #E4E8EF", borderRadius: 8, fontSize: 12.5, outline: "none", minWidth: 230, fontFamily: "inherit" }}
                onFocus={(e) => { e.target.style.borderColor = GOLD_LT; }}
                onBlur={(e) => { e.target.style.borderColor = "#E4E8EF"; }}
              />
            </div>
            <Field>
              <Select value={filterCat} onChange={(_, d) => setFilterCat(d.value)} style={{ minWidth: 140 }}>
                <option value="All">All categories</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field>
              <Select value={sort} onChange={(_, d) => setSort(d.value)} style={{ minWidth: 150 }}>
                {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Select>
            </Field>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#555", cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={onlyTracked} onChange={(e) => setOnlyTracked(e.target.checked)} />
              Repeat awards only
            </label>
          </div>
        }
      >
        {!filtered.length ? (
          <Empty>No product matches those filters.</Empty>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 940 }}>
              <thead>
                <tr style={{ background: NAVY }}>
                  <th style={th("center", { width: 34 })} title="Compare">
                    <ArrowSortDown24Regular style={{ fontSize: 13, opacity: 0.7 }} />
                  </th>
                  <th style={th("left")}>PRODUCT</th>
                  <th style={th("left")}>CATEGORY</th>
                  <th style={th("center")}>AWARDS</th>
                  <th style={th("right")}>LATEST RATE</th>
                  <th style={th("center")}>LAST MOVE</th>
                  <th style={th("center")}>PER YEAR</th>
                  <th style={th("center")}>SPREAD</th>
                  <th style={th("center")}>SUPPLIERS</th>
                  <th style={th("left")}>CONFIDENCE</th>
                  <th style={th("center", { width: 50 })}>VIEW</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((p, i) => {
                  const cc = catColor(p.category, categories);
                  const picked = compareKeys.includes(p.key);
                  return (
                    <tr
                      key={p.key}
                      style={{
                        borderBottom: "1px solid #EEF0F4",
                        background: picked ? "#FFF8EE" : i % 2 ? "#FAFBFC" : "#fff",
                        cursor: "pointer",
                      }}
                      onClick={() => setOpenKey(p.key)}
                      onMouseEnter={(e) => { if (!picked) e.currentTarget.style.background = "#FFFBF2"; }}
                      onMouseLeave={(e) => { if (!picked) e.currentTarget.style.background = i % 2 ? "#FAFBFC" : "#fff"; }}
                    >
                      <td style={td("center")} onClick={(e) => e.stopPropagation()}>
                        <Tooltip relationship="label" content={p.n < 2 ? "Needs two awards to chart" : picked ? "Remove from comparison" : "Add to comparison"}>
                          <input
                            type="checkbox" checked={picked} disabled={p.n < 2}
                            onChange={() => toggleCompare(p.key)}
                            style={{ cursor: p.n < 2 ? "not-allowed" : "pointer" }}
                          />
                        </Tooltip>
                      </td>
                      <td style={td("left", { maxWidth: 280 })}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 3, height: 26, borderRadius: 2, background: cc, flexShrink: 0 }} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: NAVY, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {p.name}
                            </div>
                            <div style={{ fontSize: 10.5, color: MUTED }}>
                              {p.unitOfMeasure || "unit not recorded"} · {p.currency}
                              {p.observations.length > 0 && (
                                <Tooltip relationship="label" content={`${p.observations.length} market observation${p.observations.length === 1 ? "" : "s"} recorded`}>
                                  <span style={{ marginLeft: 6, color: GOLD_LT }}>
                                    <Comment24Regular style={{ fontSize: 11, verticalAlign: "-1px" }} /> {p.observations.length}
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={td("left", { fontSize: 11.5, color: "#555" })}>{p.category}</td>
                      <td style={td("center", { fontWeight: 700 })}>{p.n}</td>
                      <td style={td("right", { fontWeight: 700, color: GOLD, whiteSpace: "nowrap" })}>
                        {fmtMoney(p.latestRate, p.currency, 0)}
                      </td>
                      <td style={td("center")}><ChangeBadge value={p.latestChange} /></td>
                      <td style={td("center")}>
                        {p.cagr === null
                          ? <Tooltip relationship="label" content="Needs at least six months between first and last award"><span style={{ color: "#CCC" }}>—</span></Tooltip>
                          : <ChangeBadge value={p.cagr} />}
                      </td>
                      <td style={td("center", { fontSize: 11.5, color: p.volatility > 0.35 ? WARN : "#666" })}>
                        {p.volatility === null ? "—" : `±${fmtPct(p.volatility)}`}
                      </td>
                      <td style={td("center")}>
                        <Tooltip relationship="label" content={p.suppliers.map((s) => `${s.name} (${s.awards})`).join(", ")}>
                          <span style={{
                            background: p.supplierCount === 1 && p.n > 1 ? "#FDE7E9" : p.supplierCount >= 3 ? "#EAF6EA" : "#F3F2F1",
                            color: p.supplierCount === 1 && p.n > 1 ? BAD : p.supplierCount >= 3 ? GOOD : "#666",
                            padding: "2px 9px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                          }}>
                            {p.supplierCount}
                          </span>
                        </Tooltip>
                      </td>
                      <td style={td("left")}><ConfidenceChip confidence={p.confidence} /></td>
                      <td style={td("center")} onClick={(e) => e.stopPropagation()}>
                        <Button appearance="subtle" size="small" icon={<Eye24Regular />}
                          onClick={() => setOpenKey(p.key)} style={{ color: GOLD_LT }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <Text style={{ fontSize: 11.5, color: MUTED }}>
            {filtered.length} of {products.length} product{products.length === 1 ? "" : "s"} match
            {stats.singleAward > 0 && ` · ${stats.singleAward} tendered only once so far`}
          </Text>
          {compareKeys.length > 0 && (
            <Text style={{ fontSize: 11.5, color: GOLD, fontWeight: 600 }}>
              {compareKeys.length} selected for comparison{compareKeys.length >= 5 ? " (maximum)" : ""}
            </Text>
          )}
        </div>

        {pager.isPaged && (
          <div style={{ marginTop: 8, border: "1px solid #EEF0F4", borderRadius: 9, overflow: "hidden" }}>
            <Pagination {...pager} accent={GOLD} noun="product" />
          </div>
        )}
      </Panel>

      {openProduct && (
        <ProductDetail product={openProduct} records={records} onClose={() => setOpenKey(null)} />
      )}
    </>
  );
}
